import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { jwtDecode } from 'jwt-decode';
import {
  Property10Login,
  Property1Login,
  Property2Login,
  Property3Login,
  Property4Login,
  Property5Login,
  Property6Login,
  Property7Login,
  Property8Login,
  Property9Login,
  WhiteLogo,
} from '../../../assets/images';
import { ChatModelContext, LoginContext } from '../../../utils/context';
import AuthForm from '../auth-form';
import { DecodedToken } from './login.props';
import './login.css';
import ForgotPassword from '../forgot-password';
import { AIInteractionsText, TaglineText } from '../../data/constants';
import SignUpPage from '../signup';
import {
  isGrazittiGrigoHostname,
  redirectToUnauthenticatedLanding,
} from '../../unauthenticated-redirect';

const imageSources = [
  Property1Login,
  Property3Login,
  Property4Login,
  Property5Login,
  Property6Login,
  Property2Login,
  Property7Login,
  Property8Login,
  Property9Login,
  Property10Login,
];

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLoggedIn } = useContext(LoginContext);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState<boolean>(false);
  const [signUpPageOpen, setSignUpPageOpen] = useState<boolean>(false);
  const { setChatModel } = useContext(ChatModelContext);
  const [visibleImages, setVisibleImages] = useState(Array(imageSources.length).fill(false));
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);

  // Check authentication immediately before rendering
  useEffect(() => {
    const token = localStorage.getItem('token');
    let tokenExpiry: boolean | null = null;
    if (token) {
      const decodedToken: DecodedToken = jwtDecode(JSON.parse(token));
      tokenExpiry = (decodedToken?.exp ?? 0) * 1000 < new Date().getTime();
    }
    const pushTo = localStorage.getItem('activeRoute');

    if (!tokenExpiry && token) {
      if (location.pathname === '/' || location.pathname === '/login') {
        const userDetailsJson = localStorage.getItem('user_details');
        const userDetails = userDetailsJson ? JSON.parse(userDetailsJson) : null;
        const userRole = userDetails?.role?.toLowerCase();

        if (userRole === 'admin' || userRole === 'super admin' || userRole === 'super-admin') {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/chat', { replace: true });
        }
      } else if (pushTo && location.pathname !== '/') {
        navigate(`${pushTo}`, { replace: true });
      }
    } else if (location.pathname === '/' || location.pathname === '/login') {
      const isExternal = new URLSearchParams(location.search).get('external') === 'true';
      if (isExternal) {
        sessionStorage.setItem('external', 'true');
      } else {
        sessionStorage.removeItem('external');
      }
      if (isGrazittiGrigoHostname() && !isExternal) {
        redirectToUnauthenticatedLanding();
      } else {
        setIsCheckingAuth(false);
      }
    } else {
      setIsCheckingAuth(false);
      if (location.pathname !== '/company-signup') {
        localStorage.clear();
        setChatModel('');
      }
    }
  }, [location.pathname]);

  // Set initial state based on URL path
  useEffect(() => {
    if (location.pathname === '/company-signup') {
      setSignUpPageOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    for (let i = 0; i < imageSources.length / 2; i += 1) {
      setTimeout(() => {
        setVisibleImages((prev) => {
          const newVisible = [...prev];
          newVisible[i] = true; // Left column image
          newVisible[i + 5] = true; // Right column image
          return newVisible;
        });
      }, 1000 * i); // 1s delay for each pair
    }
  }, []);

  if (isCheckingAuth) {
    return null;
  }

  const leftImages = imageSources.slice(0, 5);
  const rightImages = imageSources.slice(5);

  function renderAuthSection() {
    if (forgotPasswordOpen) {
      return <ForgotPassword setForgotPasswordOpen={setForgotPasswordOpen} />;
    }
    if (signUpPageOpen) {
      return <SignUpPage setSignUpPageOpen={setSignUpPageOpen} />;
    }
    return (
      <AuthForm
        setIsLoggedIn={setIsLoggedIn}
        setForgotPasswordOpen={setForgotPasswordOpen}
        setSignUpPageOpen={setSignUpPageOpen}
      />
    );
  }

  return (
    <main className="login bg-no-repeat bg-cover pt-4 flex flex-col w-full h-full min-h-screen items-center justify-center">
      {/* Logo */}
      <div className="flex px-6 h-12 sm:px-10 md:px-[4.25rem] md:h-20 lg:h-20 items-start mb-4 w-full justify-start">
        <img alt="GRIGO logo" src={WhiteLogo} className="h-full w-auto" />
      </div>

      <div className="flex flex-col text-white w-full text-xl px-6 sm:px-10  md:px-[5.25rem] lg:hidden">
        <span>{TaglineText}</span>
        <span className="bg-gradient-to-r from-[#FD8609] to-[#FD8709] bg-clip-text text-transparent font-extrabold">
          {AIInteractionsText}
        </span>
      </div>

      {/* Main Content */}
      <div className="flex flex-col-reverse lg:flex-row px-6 sm:px-10 md:px-[4.25rem] w-full justify-between items-center lg:items-start">
        {/* Left Content Section */}
        <div className="flex flex-col text-2xl sm:text-3xl md:text-[2.5rem] leading-snug text-white font-light items-start justify-start w-full lg:w-[65%] text-center lg:text-left">
          {/* Tagline (duplicated for left side, optional depending on screen design) */}
          <div className="hidden flex-col items-start sm:hidden lg:flex">
            <span>{TaglineText}</span>
            <span className="bg-gradient-to-r from-[#FD8609] to-[#FD8709] bg-clip-text text-transparent font-extrabold">
              {AIInteractionsText}
            </span>
          </div>

          {/* Image Columns */}
          <div className="flex flex-col md:flex-row justify-start gap-8 mt-10 w-full">
            {/* Left Column */}
            <div className="flex flex-col w-full md:w-1/2">
              {leftImages.map((src, index) => (
                <div key={`left-${src}`} className="relative flex items-center mb-8">
                  <img
                    alt={`left-${index}`}
                    src={src}
                    className={`transition-opacity duration-1000 ease-in-out transform ${
                      visibleImages[index] ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                    }`}
                  />
                  {visibleImages[index] && index > 0 && (
                    <div className="absolute left-[5%] transform -translate-x-1/2 h-8 w-px bg-slate-400 top-[-76%]" />
                  )}
                </div>
              ))}
            </div>

            {/* Right Column */}
            <div className="flex flex-col w-full md:w-1/2">
              {rightImages.map((src, index) => (
                <div key={`right-${src}`} className="relative flex items-center mb-8">
                  <img
                    alt={`right-${index}`}
                    src={src}
                    className={`transition-opacity duration-1000 ease-in-out transform ${
                      visibleImages[index + 5] ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                    }`}
                  />
                  {visibleImages[index + 5] && index > 0 && (
                    <div className="absolute left-[5%] transform -translate-x-1/2 h-8 w-px bg-slate-400 top-[-76%]" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Auth Section */}
        <div className="w-full lg:w-1/3 mt-0 lg:mt-0 rounded-xl">{renderAuthSection()}</div>
      </div>
    </main>
  );
}

export default Login;
